import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import LoginPage from "./pages/LoginPage";
import HomePage from "./pages/HomePage";
import { useSelector } from "react-redux";
import { RootState } from "./app/store/store";
import { useAppDispatch } from "./app/hooks";
import { setDarkMode } from "./features/theme/themeSlice";
import PersonalProfile from "./pages/PersonalProfile";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import AuthenticatedLayout from "./layouts/AuthenticatedLayout";
import ProfileSetting from "./pages/ProfileSetting";
import { getAccessToken, getRefreshToken } from "./features/auth/tokenStorage";
import { restoreSession, fetchAndRestoreUserData, ensureCurrentUser } from "./features/auth/authSlice";
import { SetNewPassword } from "./pages/SetNewPassword";
import AllJobs from "./pages/AllJobs";
import JobDetail from "./pages/JobOverview";
import CreateJob from "./pages/CreateJob";
import CandidateMatches from "./pages/JobMatches";
import CandidateOverview from "./pages/CandidateOverview";
import AssignRoles from './pages/AsignRoles'
import CreateMember from './pages/CreateMember'
import PrivacyPolicy from './pages/PrivacyPolicy'
import SuperAdminDash from "./pages/superAdminDash";
import CompanyList from "./pages/CompanyList";
import CompanyProfilePage from "./pages/CompanyProfile";
import ChangePassword from "./pages/ChangePassword";
import AnonymousFeedback from "./pages/AnonymousFeedback";

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const user = useSelector((state: RootState) => state.auth.user);
  const userGroupsFromStore = useSelector((state: RootState) => state.auth.userGroups);
  const [isChecking, setIsChecking] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState<string | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      // First check authentication
      if (!isAuthenticated || !accessToken) {
        setShouldRedirect("/login");
        setIsChecking(false);
        return;
      }

      // If already on reset-password page, allow access
      if (location.pathname === "/reset-password") {
        setIsChecking(false);
        return;
      }

      let userData = user;
      let resetRequired: boolean | undefined = userData?.pwd_reset_required;
      // null = not loaded yet (do not treat as super-admin — [] means API returned no groups).
      let userGroups: string[] | null = userGroupsFromStore;

      if (accessToken && userGroups === null) {
        try {
          const ensuredUser = await dispatch(ensureCurrentUser()).unwrap();
          userData = ensuredUser as typeof user;
          resetRequired = ensuredUser?.pwd_reset_required as boolean | undefined;
          userGroups = Array.isArray(ensuredUser.groups)
            ? (ensuredUser.groups as string[])
            : null;
        } catch (error) {
          console.error("Failed to fetch current user:", error);
          resetRequired = userData?.pwd_reset_required;
          userGroups =
            userData != null && Array.isArray(userData.groups)
              ? (userData.groups as string[])
              : null;
        }
      }

      // If password reset is required, redirect before group-based rules
      console.log("ProtectedRoute - Final check - resetRequired:", resetRequired, "type:", typeof resetRequired);
      if (resetRequired === true) {
        console.log("ProtectedRoute - Password reset required, redirecting to /reset-password");
        setShouldRedirect("/reset-password");
        setIsChecking(false);
        return;
      }

      // Cannot apply group-based routes until groups are known
      if (userGroups === null) {
        setShouldRedirect("/profile-setting");
        setIsChecking(false);
        return;
      }

      const groupsForRoute = userGroups;

      // If userGroups is empty array, allow access to super-admin routes
      if (groupsForRoute.length === 0) {
        // Define allowed routes for super admin
        const superAdminRoutes = [
          "/super-admin-dashboard",
          "/company-list",
          "/company-profile",
          "/all-members",
          "/create-member",
        ];
        
        // Check if the path matches any super admin route (including dynamic routes)
        const isAllowedRoute = superAdminRoutes.some(route => 
          location.pathname === route || location.pathname.startsWith(route + '/')
        );
        
        if (!isAllowedRoute) {
          console.log("ProtectedRoute - User has no groups, redirecting to /super-admin-dashboard");
          setShouldRedirect("/super-admin-dashboard");
          setIsChecking(false);
          return;
        }
        // User is on an allowed super-admin route, allow access
        setIsChecking(false);
        return;
      }

      // Check if company_admin is trying to access dashboard - redirect to profile-setting
      if (groupsForRoute.includes("company_admin") && location.pathname === "/dashboard") {
        console.log("ProtectedRoute - company_admin trying to access dashboard, redirecting to /profile-setting");
        setShouldRedirect("/profile-setting");
        setIsChecking(false);
        return;
      }

      // Prevent non-super-admins from accessing *super admin only* routes
      const superAdminOnlyRoutes = [
        "/super-admin-dashboard",
        "/company-list",
        "/company-profile",
      ];
      
      // Check if the path matches any super admin route (including dynamic routes)
      const isSuperAdminOnlyRoute = superAdminOnlyRoutes.some(route => 
        location.pathname === route || location.pathname.startsWith(route + '/')
      );
      
      if (isSuperAdminOnlyRoute && groupsForRoute.length > 0) {
        console.log("ProtectedRoute - Non-super-admin trying to access super admin route, redirecting to /profile-setting");
        setShouldRedirect("/profile-setting");
        setIsChecking(false);
        return;
      }

      // Password reset not required, allow access
      setIsChecking(false);
    };

    checkAccess();
  }, [isAuthenticated, user, userGroupsFromStore, accessToken, location.pathname, dispatch]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#121212]">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (shouldRedirect) {
    return <Navigate to={shouldRedirect} replace />;
  }

  return <>{children}</>;
};

// Component to handle redirects with async user fetching
const AuthRedirect: React.FC<{ defaultPath: string }> = ({ defaultPath }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const user = useSelector((state: RootState) => state.auth.user);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const userGroupsFromStore = useSelector((state: RootState) => state.auth.userGroups);
  const dispatch = useAppDispatch();
  const [targetPath, setTargetPath] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
      const determineRedirect = async () => {
      
        if (!isAuthenticated) {
          setTargetPath("/login");
          setIsChecking(false);
          return;
        }

        let userData = user;
        let resetRequired: boolean | undefined = userData?.pwd_reset_required;
        let userGroups: string[] | null = userGroupsFromStore;

        if (accessToken && userGroups === null) {
          try {
            const ensuredUser = await dispatch(ensureCurrentUser()).unwrap();
            userData = ensuredUser as typeof user;
            resetRequired = ensuredUser?.pwd_reset_required as boolean | undefined;
            userGroups = Array.isArray(ensuredUser.groups)
              ? (ensuredUser.groups as string[])
              : null;
          } catch (error) {
            console.error("Failed to fetch current user:", error);
            resetRequired = userData?.pwd_reset_required;
            userGroups =
              userData != null && Array.isArray(userData.groups)
                ? (userData.groups as string[])
                : null;
          }
        }

        // Determine redirect path (password reset before group rules)
        console.log("AuthRedirect - Final check - resetRequired:", resetRequired, "type:", typeof resetRequired);
        if (resetRequired === true) {
          console.log("AuthRedirect - Password reset required, redirecting to /reset-password");
          setTargetPath("/reset-password");
        } else if (userGroups === null) {
          setTargetPath("/profile-setting");
        } else if (userGroups.length === 0) {
          // If userGroups is empty array, redirect to super-admin-dashboard
          console.log("AuthRedirect - User has no groups, redirecting to /super-admin-dashboard");
          setTargetPath("/super-admin-dashboard");
        } else {
          const g = userGroups;
          // Routing rule:
          // - company_recruiter or company_hiring_manager -> dashboard
          // - everyone else -> profile-setting
          const canAccessDashboard = g.includes("company_recruiter") || g.includes("company_hiring_manager");
          const finalPath = canAccessDashboard ? "/dashboard" : "/profile-setting";
          console.log("AuthRedirect - Redirecting to:", finalPath);
          setTargetPath(finalPath);
        }

        setIsChecking(false);
      };

      determineRedirect();
  }, [isAuthenticated, user, userGroupsFromStore, accessToken, defaultPath, dispatch]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#121212]">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return <Navigate to={targetPath || defaultPath} replace />;
};

const App: React.FC = () => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated,
  );
  const dispatch = useAppDispatch();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    dispatch(setDarkMode(mediaQuery.matches));

    const handleChange = (e: MediaQueryListEvent) => {
      dispatch(setDarkMode(e.matches));
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [dispatch]);

  useEffect(() => {
    // Check for tokens and restore session on mount
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    if (accessToken && refreshToken && !isAuthenticated) {
      dispatch(restoreSession({ accessToken, refreshToken }));
      setTimeout(() => {
        dispatch(fetchAndRestoreUserData());
      }, 0);
    }
    setIsCheckingAuth(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#121212]">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/reset-password" element={<SetNewPassword />} />
        <Route path="/reset-password/:token" element={<SetNewPassword />} />
        <Route path="/anonymous-feedback" element={<AnonymousFeedback />} />
        <Route
          element={
            <ProtectedRoute>
              <AuthenticatedLayout />
            </ProtectedRoute>
          }
        >
            <Route path="/profile-setting" element={<ProfileSetting />} />
            <Route path="/dashboard" element={<HomePage />} />
            <Route path="/all-jobs" element={<AllJobs />} />
            <Route path="/create-job" element={<CreateJob />} />
            <Route path="/edit-job/:id" element={<CreateJob />} />
            <Route path="/job-matches" element={<CandidateMatches />} />
            <Route path="/personal-profile" element={<PersonalProfile />} />
            <Route path="/candidate/:id" element={<CandidateOverview />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/all-members" element={<AssignRoles />} />
            <Route path="/create-member" element={<CreateMember />} />
            <Route path="/super-admin-dashboard" element={<SuperAdminDash />} />
            <Route path="/company-list" element={<CompanyList />} />
            <Route path="/company-profile" element={<CompanyProfilePage />} />
            <Route path="/company-profile/:id" element={<CompanyProfilePage />} />
            <Route path="/change-password" element={<ChangePassword />} />
          </Route>

          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <AuthRedirect defaultPath="/dashboard" />
              ) : (
                <LoginPage />
              )
            }
          />
          <Route
            path="/signUp"
            element={
              isAuthenticated ? (
                <AuthRedirect defaultPath="/dashboard" />
              ) : (
                <SignUp />
              )
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? (
                <AuthRedirect defaultPath="/dashboard" />
              ) : (
                <ForgotPassword />
              )
            }
          />
          <Route
            path="*"
            element={
              <AuthRedirect
                defaultPath={isAuthenticated ? "/dashboard" : "/login"}
              />
            }
          />
        </Routes>
      <Toaster richColors position="top-right" expand />
    </BrowserRouter>
  );
};

export default App;
